import { InputRef } from '@unistocks/components/basic/input';
import { Form } from '@unistocks/components/form/form';
import { WatchValues } from '@unistocks/components/form/components/watch-values';
import {
  applyRefinements,
  conditionalRequired,
  createMoreThanSchema
} from '@unistocks/components/form/validators';
import { PORTFOLIO, PortfolioType, WATCHLIST } from '@unistocks/features/portfolios/portfolio-type';
import { t } from '@unistocks/localization';
import { AssetType, SectionAssetDto, SectionAssetUpdateDto } from '@unistocks/proxy/axios-client';
import { isDefined, isNumber } from '@unistocks/shared/utils/util';
import { FormPropsBase } from '@unistocks/types/form-props';
import { Nullable } from '@unistocks/types/util/nullable';
import { memo, useCallback, useMemo, useRef } from 'react';
import { z } from 'zod';

export type EditAssetFormProps = FormPropsBase<SectionAssetUpdateDto> & {
  portfolioType: PortfolioType;
  portfolioCurrency: string;
  asset: SectionAssetDto;
};

const quantityRequired = (asset: SectionAssetDto) => (totalValue: number) => {
  if (asset?.isSynced) return false;
  if ([AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(asset?.type))
    return false;
  if (isNumber(asset?.totalValue ?? totalValue)) return false;

  return true;
};

const currentPriceRequired = (asset: SectionAssetDto) => (totalValue: number) => {
  if (isDefined(asset.symbol)) return false;

  if ([AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(asset.type)) return false;

  if (isNumber(asset?.totalValue) || isNumber(totalValue)) return false;

  return true;
};

const totalValueRequired = (asset: SectionAssetDto) => (currentPrice: number) => {
  if (asset?.isListed) return false;
  if (asset?.isAccount && asset?.isSynced) return false;
  if (isNumber(asset?.quantity) || isNumber(asset?.currentPrice ?? currentPrice)) return false;
  if ([AssetType.Stock, AssetType.Fund, AssetType.CryptoCurrency].includes(asset?.type))
    return false;

  return true;
};

const portfolioAssetschemaFactory = (asset: SectionAssetDto) =>
  applyRefinements(
    z.object({
      name: z.string().nullish(),
      quantity: createMoreThanSchema(0, t('::Quantity')).nullish(),
      currentPrice: createMoreThanSchema(0, t('::AssetPrice')).nullish(),
      totalValue: createMoreThanSchema(0, t('::TotalValue')).nullish(),
      averageAcquisitionPrice: createMoreThanSchema(0, t('::AverageAcquisitionPrice')).nullish()
    }),
    conditionalRequired(quantityRequired(asset), 'quantity', { fieldName: t('::Quantity') }),
    conditionalRequired(currentPriceRequired(asset), 'currentPrice', {
      fieldName: t('::AssetPrice'),
      dependsOn: ['totalValue']
    }),
    conditionalRequired(totalValueRequired(asset), 'totalValue', {
      fieldName: t('::TotalValue'),
      dependsOn: ['currentPrice']
    })
  );

const watchlistAssetschemaFactory = () =>
  z.object({
    name: z.string().nullish()
  });

export const EditSectionAssetForm = memo<EditAssetFormProps>(
  ({ portfolioType, portfolioCurrency, asset, isSubmitting, onSubmit }) => {
    const nameRef = useRef<InputRef>(null);
    const quantityRef = useRef<InputRef>(null);
    const currentPriceRef = useRef<InputRef>(null);
    const totalValueRef = useRef<InputRef>(null);
    const averageAcquisitionPriceRef = useRef<InputRef>(null);

    const schema = useMemo(
      () =>
        asset
          ? portfolioType === PORTFOLIO
            ? portfolioAssetschemaFactory(asset)
            : watchlistAssetschemaFactory()
          : undefined,
      [asset, portfolioType]
    );

    const initialValues = useMemo<Partial<SectionAssetUpdateDto>>(
      () => ({
        name: asset?.name,
        quantity: asset?.quantity,
        currentPrice: asset?.currentPrice,
        averageAcquisitionPrice: asset?.averageAcquisitionPrice?.value,
        totalValue: asset?.totalValue
      }),
      [asset]
    );

    const quantityDisabled = useCallback(
      (totalValue: Nullable<number>) => {
        return (
          portfolioType === WATCHLIST ||
          isDefined(asset?.totalValue ?? totalValue) ||
          [AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(asset.type)
        );
      },
      [asset, portfolioType]
    );

    const isQuantityRequired = useCallback(
      (totalValue: number) => quantityRequired(asset)(totalValue),
      [asset]
    );

    const currentPriceDisabled = useCallback(
      (totalValue: number) => {
        return (
          portfolioType === WATCHLIST ||
          isDefined(asset?.totalValue ?? totalValue) ||
          isDefined(asset?.symbol) ||
          [AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(asset?.type)
        );
      },
      [asset, portfolioType]
    );

    const isCurrentPriceRequired = useCallback(
      (totalValue: number) => currentPriceRequired(asset)(totalValue),
      [asset]
    );

    const totalValueDisabled = useCallback(
      (quantity: Nullable<number>, currentPrice: Nullable<number>) => {
        return (
          portfolioType === WATCHLIST ||
          asset?.isListed ||
          (asset?.isAccount && asset?.isSynced) ||
          isDefined(asset?.quantity ?? quantity) ||
          isDefined(asset?.currentPrice ?? currentPrice) ||
          [AssetType.Stock, AssetType.Fund, AssetType.CryptoCurrency].includes(asset?.type)
        );
      },
      [asset, portfolioType]
    );

    const isTotalValueRequired = useCallback(
      (quantity: number, currentPrice: number) => totalValueRequired(asset)(currentPrice),
      [asset]
    );

    return (
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        isLoading={isSubmitting}
        schema={schema}
      >
        {(form, submit) => (
          <>
            <WatchValues<SectionAssetUpdateDto> names={['totalValue', 'quantity', 'currentPrice']}>
              {([totalValue, quantity, currentPrice]) => (
                <Form.Input
                  ref={nameRef}
                  name={'name'}
                  label={t('::Name')}
                  isRequired={portfolioType === PORTFOLIO && !asset?.isSynced}
                  returnKeyType={
                    !quantityDisabled(totalValue) || !totalValueDisabled(quantity, currentPrice)
                      ? 'next'
                      : 'send'
                  }
                  onSubmitEditing={() =>
                    quantityRef.current
                      ? quantityRef.current.focus()
                      : totalValueRef.current
                        ? totalValueRef.current.focus()
                        : submit()
                  }
                  helpText={
                    portfolioType === WATCHLIST
                      ? t('::Watchlist:EditAsset:Name:HelpText')
                      : asset.isListed
                        ? t('::Portfolio:EditAsset:Name:HelpText')
                        : undefined
                  }
                  valueTransform={{
                    output: value => (isDefined(value) ? value : null)
                  }}
                />
              )}
            </WatchValues>

            <Form.Input
              ref={quantityRef}
              name={'quantity'}
              label={t('::Quantity')}
              helpText={asset?.isSynced ? t('::Asset:Quantity:HelpText') : undefined}
              keyboardType={'numeric'}
              isRequired={isQuantityRequired}
              hidden={quantityDisabled}
              dependsOn={'totalValue'}
              returnKeyType={'next'}
              onSubmitEditing={() =>
                currentPriceRef.current
                  ? currentPriceRef.current.focus()
                  : averageAcquisitionPriceRef.current?.focus()
              }
            />

            <Form.Input
              ref={currentPriceRef}
              name={'currentPrice'}
              label={t('::AssetPrice')}
              keyboardType={'numeric'}
              isRequired={isCurrentPriceRequired}
              hidden={currentPriceDisabled}
              appendText={asset?.currency}
              dependsOn={'totalValue'}
              returnKeyType={'next'}
              onSubmitEditing={() => averageAcquisitionPriceRef.current?.focus()}
            />

            <Form.Input
              ref={averageAcquisitionPriceRef}
              name={'averageAcquisitionPrice'}
              label={t('::AverageAcquisitionPrice')}
              helpText={t('::Asset:AverageAcquisitionPrice:HelpText')}
              keyboardType={'numeric'}
              hidden={quantityDisabled}
              appendText={portfolioCurrency}
              dependsOn={'totalValue'}
              returnKeyType={'send'}
              onSubmitEditing={() => submit()}
            />

            <Form.Input
              ref={totalValueRef}
              name={'totalValue'}
              label={t('::TotalValue')}
              keyboardType={'numeric'}
              isRequired={isTotalValueRequired}
              hidden={totalValueDisabled}
              appendText={asset?.currency}
              dependsOn={['quantity', 'currentPrice']}
              returnKeyType={'send'}
              onSubmitEditing={() => submit()}
            />
          </>
        )}
      </Form>
    );
  }
);

EditSectionAssetForm.displayName = 'EditSectionAssetForm';
